import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Space, TimePicker, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../api_actions/index';
import { useRoleNameStore, useIsSuperAdmin,useDeploymentListStore } from '../../store';
import { REGEX_PATTERNS } from '../../constants/enums';
const { Text, Title, Link } = Typography;

const DAYS = [
  { day_name: 'Monday', day_id: 0 },
  { day_name: 'Tuesday', day_id: 1 },
  { day_name: 'Wednesday', day_id: 2 },
  { day_name: 'Thursday', day_id: 3 },
  { day_name: 'Friday', day_id: 4 },
  { day_name: 'Saturday', day_id: 5 },
  { day_name: 'Sunday', day_id: 6 }
];

const DEFAULT_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2'];

const Scheduler = ({
  deployment_key,
  createEndpoint,
  readEndpoint,
  editEndpoint,
  deleteEndpoint,
  detectionPackagesEndpoint,
  location_name,
}) => {
  const [schedules, setSchedules] = useState([]);
  const [detectionPackages, setDetectionPackages] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const roleName = useRoleNameStore((state) => state.roleName);
  const isSuperAdmin = useIsSuperAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const deployments = useDeploymentListStore((state) => state.deployments);
  const currentIndex = deployments.findIndex(
      d => String(d.deployment_id) === String(deployment_key)
  );

  const handleNavigate = (newIndex) => {
      const target = deployments[newIndex];
      navigate(`/schedule/${target.deployment_id}/${target.location_name}`, {
          state: { deployments } // carry the list forward
      });
  };

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < deployments.length - 1;

  const loadSchedules = async () => {
    try {
      const result = await apiClient.post(readEndpoint);
      result.data?.forEach(stream => {
        stream.detection_package_id = stream.detection_package.detection_package_id
      });
      setSchedules(result.data || []);
    } catch (error) {
      console.error('Failed to load schedules', error);
    }
  };

  const loadDetectionPackages = async () => {
    try {
      const result = await apiClient.post(detectionPackagesEndpoint);

      setDetectionPackages(result.data || []);
    } catch (error) {
      console.error('Failed to load detection types', error);
    }
  };

  useEffect(() => {
    loadSchedules();
    loadDetectionPackages();
  }, []);

  const timeStringToDecimal = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours + minutes / 60 + seconds / 3600;
  };


  // Convert decimal hours to "HH:MM:SS" string (e.g., 7.5 -> "07:30:00")
  const decimalToTimeString = (decimal) => {
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  };

  const saveSchedule = async (values) => {
    try {
      const result = await apiClient.post(createEndpoint, values);
      await loadSchedules();
      return result;
    } catch (error) {
      console.error('Failed to save schedule', error);
    }
  };

  const updateSchedule = async (values) => {
    try {
      const result = await apiClient.post(editEndpoint, values);
      await loadSchedules();
      return result;
    } catch (error) {
      console.error('Failed to update schedule', error);
    }
  };

  const deleteSchedule = async (values) => {
    try {
      const result = await apiClient.post(deleteEndpoint, values);
      await loadSchedules();
      return result;
    } catch (error) {
      console.error('Failed to delete schedule', error);
    }
  };

  const getDayName = (dayId) => {
    const day = DAYS.find(d => d.day_id === dayId);
    return day ? day.day_name : '';
  };

  const handleDayClick = (dayObj, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedHour = (x / rect.width) * 24;

    const newSchedule = {
      id: Date.now(),
      schedule_id: null,
      day_ids: [dayObj.day_id],
      start_time: decimalToTimeString(Math.floor(clickedHour * 2) / 2),
      end_time: decimalToTimeString(Math.min(Math.floor(clickedHour * 2) / 2 + 2, 24)),
      detection_package_id: detectionPackages.length > 0 ? detectionPackages[0].detection_package_id : 1,
      name: 'New Schedule',
      color: DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)]
    };

    console.log(newSchedule)

    setEditForm(newSchedule);
    setSelectedSchedule(newSchedule);
    setIsModalOpen(true);
  };

  const handleBarClick = (schedule, e) => {
    e.stopPropagation();
    setSelectedSchedule(schedule);
    setEditForm({ ...schedule });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const original_schedule = {...getSchedulesForID(editForm.schedule_id)[0]} //Making a shallow clone to avoid in memory mutation of the same object
    editForm.day_ids.map(day_id => {
      var scheduleData = {}

      if (editForm.schedule_id){ //Means we are editing since the item is already defined
        scheduleData = getSchedulesForDay(day_id)
        scheduleData.map(schedule_data => {
          if (schedule_data.detection_package_id === original_schedule.detection_package_id && schedule_data.start_time === original_schedule.start_time && schedule_data.end_time === original_schedule.end_time){
            if (!scheduleData || Object.keys(scheduleData).length === 0) {
              return; // 'return' in forEach/map acts like 'continue' — skips to next iteration
            }

            schedule_data.start_time = String(editForm.start_time)
            schedule_data.end_time = String(editForm.end_time)
            schedule_data.detection_package_id = editForm.detection_package_id
            updateSchedule(schedule_data);
          }
        })
        
        // Correctly check if scheduleData is missing/empty

      } else {
        scheduleData = {
          schedule_id: editForm.schedule_id,
          day_ids: [day_id],
          start_time: String(editForm.start_time),
          end_time: String(editForm.end_time),
          detection_package_id: editForm.detection_package_id,
        };
      }

      if (!editForm.schedule_id) {
        saveSchedule(scheduleData)
      }
    })
    setIsModalOpen(false);
    setSelectedSchedule(null);
  };

  const handleDelete = async () => {
    editForm.day_ids.map(day_id => {
      const scheduleData = getSchedulesForDay(day_id)
      scheduleData.map(schedule_data => {
        if (schedule_data.detection_package_id === editForm.detection_package_id && schedule_data.start_time === editForm.start_time && schedule_data.end_time === editForm.end_time){
          deleteSchedule(schedule_data);
        }
      })
      
    })
    setIsModalOpen(false);
    setSelectedSchedule(null);
  };

  const getSchedulesForID = (schedule_id) => {
    return schedules.filter(m => m.schedule_id === schedule_id);
  };

  const getSchedulesForDay = (dayId) => {
    return schedules.filter(m => m.day_ids[0] === dayId);
  };

  const getSchedulePosition = (schedule, daySchedules) => {
    const sortedSchedules = [...daySchedules].sort((a, b) => {
      const timeA = timeStringToDecimal(a.start_time);
      const timeB = timeStringToDecimal(b.start_time);
      return timeA !== timeB ? timeA - timeB : (a.schedule_id || a.id) - (b.schedule_id || b.id)
    });

    const layers = [];

    for (let m of sortedSchedules) {
      let layer = 0;
      while (layers[layer]) {
        const hasOverlap = layers[layer].some(existing => {
          const existingEnd = timeStringToDecimal(existing.end_time);
          const existingStart = timeStringToDecimal(existing.start_time);
          const mEnd = timeStringToDecimal(m.end_time);
          const mStart = timeStringToDecimal(m.start_time);
          return !(existingEnd <= mStart || mStart >= existingEnd);
        });
        if (!hasOverlap) break;
        layer++;
      }

      if (!layers[layer]) layers[layer] = [];
      layers[layer].push(m);

      if ((m.schedule_id || m.id) === (schedule.schedule_id || schedule.id)) {
        return { layer, totalLayers: layers.length };
      }
    }

    return { layer: 0, totalLayers: 1 };
  };

  return (
    <div className='container-wrapper' style={{fontFamily: 'sans-serif' }}>
          {/* Nav row — stretches to match chart width below */}
      <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
      }}>
          {/* <Button
              onClick={() => handleNavigate(currentIndex - 1)}
              disabled={!hasPrev}
          >
              &larr; Previous
          </Button> */}

          <div style={{
              flex: 1,
              textAlign: 'left',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              padding: '0 16px',
          }}>
              <h2 style={{ margin: 0 }}>{location_name}</h2>
          </div>

          {/* <Button
              onClick={() => handleNavigate(currentIndex + 1)}
              disabled={!hasNext}
          >
              Next &rarr;
          </Button> */}
      </div>

      <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
        {/* Header with hours */}
        <div style={{ display: 'flex', borderBottom: '2px solid #d9d9d9' }}>
          <div style={{ width: '120px', padding: '10px', background: '#fafafa', fontWeight: 'bold', borderRight: '1px solid #d9d9d9' }}>
            Day
          </div>
          <div style={{ flex: 1, display: 'flex', position: 'relative', background: '#fafafa' }}>
            {Array.from({ length: 24 }, (_, i) => (
              <div key={i} style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '12px',
                padding: '10px 0',
                borderRight: i < 23 ? '1px solid #e8e8e8' : 'none'
              }}>
                {String(i).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>

        {/* Days rows */}
        {DAYS.map(dayObj => {
          const daySchedules = getSchedulesForDay(dayObj.day_id);
          const maxLayers = Math.max(1, ...daySchedules.map(m => getSchedulePosition(m, daySchedules).totalLayers));

          return (
            <div key={dayObj.day_id} style={{ display: 'flex', borderBottom: '1px solid #d9d9d9', minHeight: '60px' }}>
              <div style={{
                width: '120px',
                padding: '10px',
                fontWeight: '500',
                borderRight: '1px solid #d9d9d9',
                display: 'flex',
                alignItems: 'center'
              }}>
                {dayObj.day_name}
              </div>
              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  minHeight: `${maxLayers * 36 + 20}px`,
                  cursor: isSuperAdmin ? 'pointer' : 'not-allowed',
                  background: '#fff'
                }}
                onClick={isSuperAdmin ? (e) => handleDayClick(dayObj, e) : undefined}
              >
                {/* Hour grid lines */}
                {Array.from({ length: 23 }, (_, i) => (
                  <div key={i} style={{
                    position: 'absolute',
                    left: `${((i + 1) / 24) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    background: '#f0f0f0'
                  }} />
                ))}

                {/* Schedule bars */}
                {daySchedules.map(schedule => {
                  const { layer, totalLayers } = getSchedulePosition(schedule, daySchedules);
                  const barHeight = 32;
                  const gap = 4;

                  return (
                    <div
                      key={schedule.schedule_id || schedule.id}
                      onClick={isSuperAdmin ? (e) => handleBarClick(schedule, e) : undefined}
                      style={{
                        position: 'absolute',
                        left: `${(timeStringToDecimal(schedule.start_time) / 24) * 100}%`,
                        width: `${((timeStringToDecimal(schedule.end_time) - timeStringToDecimal(schedule.start_time)) / 24) * 100}%`,
                        top: `${layer * (barHeight + gap) + 10}px`,
                        height: `${barHeight}px`,
                        background: schedule.detection_package.color || DEFAULT_COLORS[0],
                        borderRadius: '4px',
                        padding: '6px 8px',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: isSuperAdmin ? 'pointer' : 'not-allowed',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'transform 0.1s, box-shadow 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      {schedule?.detection_package?.detection_package_name || getDayName(schedule.day_ids[0])}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        title={selectedSchedule && selectedSchedule.schedule_id ? "Edit Schedule" : "Create Schedule"}
        open={isModalOpen}
        onOk={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedSchedule(null);
        }}
        footer={[
          selectedSchedule && selectedSchedule.schedule_id && (
            <Button key="delete" danger onClick={handleDelete}>
              Delete
            </Button>
          ),
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Day</label>
            <Select
              style={{ width: '100%' }}
              value={editForm.day_ids}
              mode={'multiple'}
              showSearch={true}
              onChange={(value) => setEditForm({ ...editForm, day_ids: value })} //for each day set value if we are doing this with multiple vals
            >

              {DAYS.map(day => (
                <Select key={day.day_id} value={day.day_id} >{day.day_name}</Select>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Detection Package</label>
            <Select
              style={{ width: '100%' }}
              value={editForm.detection_package_id}
              onChange={(value) => setEditForm({ ...editForm, detection_package_id: value })}
            >
              {detectionPackages.map(type => (
                <Select.Option key={type.detection_package_id} value={type.detection_package_id}>
                  {type.detection_package_name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Start Time</label>
            <TimePicker
              style={{ width: '100%' }}
              format="HH:mm"
              minuteStep={15}
              value={editForm.start_time ? dayjs(editForm.start_time, 'HH:mm:ss') : null}
              onChange={(time) => {
                if (time) {
                  setEditForm({ ...editForm, start_time: time.format('HH:mm:00') });
                }
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>End Time</label>
            <TimePicker
              style={{ width: '100%' }}
              format="HH:mm"
              minuteStep={15}
              value={editForm.end_time ? dayjs(editForm.end_time, 'HH:mm:ss') : null}
              onChange={(time) => {
                if (time) {
                  setEditForm({ ...editForm, end_time: time.format('HH:mm:00') });
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Scheduler;